import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, Trash2, Package, LayoutGrid, List, PackagePlus, Pencil, FileSpreadsheet, FileDown, Printer, Mail, PanelLeftClose, PanelLeftOpen, UserCheck, ChevronLeft, ChevronRight, GripVertical, RefreshCw, Link as LinkIcon, BellRing, Columns3, Copy, Maximize2, Minimize2, ArrowUpToLine } from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { photoThumb, photoThumbBackend } from "@/lib/photoUrl";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { useQuote } from "@/context/QuoteContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import QuoteItemEditorDialog from "@/components/QuoteItemEditorDialog";
import CustomerQuickDialog from "@/components/CustomerQuickDialog";
import QuoteEditDialog from "@/components/quotes/QuoteEditDialog";
import ProcurementLinksDialog from "@/components/quotes/ProcurementLinksDialog";
import QuoteItemsTable from "@/components/quotes/QuoteItemsTable";
import BuyerQuoteItems from "@/components/quotes/BuyerQuoteItems";
import QuoteItemsMobile from "@/components/quotes/QuoteItemsMobile";
import QuoteMobileActions from "@/components/quotes/QuoteMobileActions";
import { ExportColumnsDialog } from "@/components/quotes/ExportColumnsDialog";
import AutoGrowTextarea from "@/components/quotes/AutoGrowTextarea";
import BackfillPricesDialog from "@/components/quotes/BackfillPricesDialog";

// factory cost with its entry currency (USD when entered in USD, otherwise RMB)
const fmtRowCost = (r) => {
  const cur = r.cost_currency === "USD" || (!r.cost_currency && (r.factory_cost_usd || 0) > 0) ? "USD" : "RMB";
  const val = cur === "USD" ? r.factory_cost_usd : r.factory_cost;
  if (!val) return "-";
  return `${val.toFixed(2)} ${cur}`;
};

/** 2026-09 · 产品清单全屏 — portal to <body> so the app header/sidebar don't sit on top. */
const FullscreenBox = ({ active, children }) => {
  if (!active) return <div data-testid="quote-items-wrapper">{children}</div>;
  return createPortal(
    <div data-testid="quote-items-wrapper"
      className="fixed inset-0 z-40 bg-background p-2 overflow-hidden">
      {children}
    </div>,
    document.body,
  );
};

export default function Quotes() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  // 2026-06 · 采购 / 采购经理 get a READ-ONLY quote view: only quotes containing
  // their own products, only those rows, and a 4-column list (单号/客户/外销/日期).
  const isBuyerView = ["buyer", "procurement_manager"].includes(user?.role);
  const readOnly = user?.role === "customer" || isBuyerView;
  const navigate = useNavigate();
  const { activeQuote, selectQuote, refreshCount } = useQuote();
  const [quotes, setQuotes] = useState([]);
  const [selectedId, setSelectedId] = useState(activeQuote?.id || null);
  const [detail, setDetail] = useState(null);
  const [exportColsOpen, setExportColsOpen] = useState(false);
  // 2026-09 · 产品清单全屏 (user spec)
  const [itemsFullscreen, setItemsFullscreen] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [view, setView] = useState("list");
  const [listCollapsed, setListCollapsed] = useState(() => localStorage.getItem("quotesys_quote_list_collapsed") === "1");
  const [customers, setCustomers] = useState([]);
  // Full customer docs (name / currency / price_multiplier) — used by the quote
  // edit dialog's 新增客户 / 编辑客户 buttons and the customer summary card.
  const [customerObjs, setCustomerObjs] = useState([]);
  const [customerDialog, setCustomerDialog] = useState(null); // null | { customer } | {}
  const [shippingFee, setShippingFee] = useState(null);
  const [globalRate, setGlobalRate] = useState(null);
  const [projects, setProjects] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("__all__");
  const [projectFilter, setProjectFilter] = useState("__all__");
  const [sellerFilter, setSellerFilter] = useState("__all__");
  const [copyingQuote, setCopyingQuote] = useState(null);
  const [editing, setEditing] = useState(null); // quote being edited in dialog
  // Users that can receive a project share (admin + export, excluding self).
  // Loaded once when the edit dialog first opens; the checkbox state ``editingShareWith``
  // mirrors the existing share doc for the quote's project so the user can add/remove
  // recipients in the same "编辑报价单" flow — see handleSaveEdit for the save behaviour.
  const [shareUsers, setShareUsers] = useState([]);
  const [editingShareWith, setEditingShareWith] = useState(new Set());
  // 2026-02-27 · Subset of ``editingShareWith`` who ALSO get edit access
  // (per-user 只读 / 只读和编辑 dropdown, per user spec).
  const [editingShareEditors, setEditingShareEditors] = useState(new Set());
  const fmtDate = (dt) => (dt ? new Date(dt).toLocaleDateString(lang === "zh" ? "zh-CN" : "en-US") : "-");

  // Customer record backing the quote currently open in the edit dialog.
  const selectedCustomerObj = customerObjs.find((c) => c.name === editing?.customer_name) || null;

  // A customer saved from inside the quote dialog becomes the quote's customer
  // right away, so 新增客户 → 保存 needs no extra click.
  const handleCustomerSaved = async (saved) => {
    await fetchCustomers();
    if (saved?.name) setEditing((q) => (q ? { ...q, customer_name: saved.name } : q));
  };

  // 2026-10-26 · 名片保存后一键开报价单草稿 —— 用新客户名字直接 POST /quotes，
  //  关掉所有对话框，选中并打开那张新单。
  const handleCustomerSavedAndCreateQuote = async (saved) => {
    if (!saved?.name) return;
    try {
      await fetchCustomers();
      const { data } = await api.post("/quotes", { customer_name: saved.name });
      toast.success(`${t("quote.created")}: ${data.quote_number}`);
      // 关掉编辑态和客户对话框，然后选中新单
      setEditing(null);
      setCustomerDialog(null);
      await fetchQuotes();
      setSelectedId(data.id);
      selectQuote(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Client-side filter for the sidebar (fast — quotes list is capped at 500 rows).
  // 2026-06 · 外销 options come straight from the loaded quotes (always in scope).
  const sellers = [...new Set(quotes.map((q) => (q.seller_name || "").trim()).filter(Boolean))].sort();

  const filteredQuotes = quotes.filter((q) => {    if (customerFilter !== "__all__" && (q.customer_name || "") !== customerFilter) return false;
    if (sellerFilter !== "__all__" && (q.seller_name || "") !== sellerFilter) return false;
    if (projectFilter !== "__all__" && (q.project || "") !== projectFilter) return false;
    return true;
  });

  // Combined "no writes allowed" flag — covers 客户 role AND shared VIEWERS
  // (shared_with but not shared_editors). Shared editors get full edit UI:
  // backend exposes ``can_edit_shared`` when the current user is in the
  // quote's ``shared_editors`` list.
  const detailReadOnly = readOnly || (Boolean(detail?.is_shared) && !detail?.can_edit_shared);

  // 2026-07-29 · Admin default is "only my quotes" — otherwise two admins
  // would see identical lists. A toggle button lets admins flip to "all".
  const isAdmin = user?.role === "admin";
  const [adminScope, setAdminScope] = useState(
    () => localStorage.getItem("quotesys_quote_admin_scope") || "mine",
  );
  useEffect(() => {
    localStorage.setItem("quotesys_quote_admin_scope", adminScope);
  }, [adminScope]);

  const fetchQuotes = useCallback(async () => {
    try {
      const { data } = await api.get("/quotes", {
        params: isAdmin ? { scope: adminScope } : undefined,
      });
      setQuotes(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [isAdmin, adminScope]);

  const fetchDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null);
      return;
    }
    try {
      const { data } = await api.get(`/quotes/${id}`);
      setDetail(data);
      // 2026-06 · Stamp "seen" so the green 采购已填报 badge starts its 2-day
      // countdown from the moment the 外销 actually opened the quote.
      const needsStamp = (data.items || []).some(
        (it) => (it.procurement_filled_count || 0) > 0 && !it.procurement_filled_seen_at,
      );
      if (needsStamp && !isBuyerView) {
        api.post(`/quotes/${id}/procurement-seen`).catch((e) =>
          console.warn("procurement-seen stamp failed (non-blocking):", e));
      }
    } catch (error) {
      console.warn("Failed to load quote detail, clearing selection:", error);
      setDetail(null);
      setSelectedId(null);
    }
  }, [isBuyerView]);

  const fetchCustomers = useCallback(async () => {
    if (readOnly) return;
    try {
      const { data } = await api.get("/quotes/meta/customers");
      setCustomers(data.customers || []);
    } catch (error) {
      console.warn("Failed to load customer options (non-blocking):", error);
    }
    // Full customer records power the 新增/编辑客户 flow + the summary card
    // (币种 / 价格系数) inside the quote edit dialog.
    try {
      const { data } = await api.get("/customers");
      setCustomerObjs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.warn("Failed to load customer records (non-blocking):", error);
    }
  }, [readOnly]);

  const fetchProjects = useCallback(async () => {
    if (readOnly) return;
    try {
      const { data } = await api.get("/quotes/meta/projects");
      setProjects(data.projects || []);
    } catch (error) {
      console.warn("Failed to load project options (non-blocking):", error);
    }
  }, [readOnly]);

  useEffect(() => {
    fetchQuotes();
    fetchCustomers();
    fetchProjects();
  }, [fetchQuotes, fetchCustomers, fetchProjects]);

  // Global 海运费 (settings) — shown on the customer summary card so the user
  // can see at a glance that freight is configured for the price calculation.
  useEffect(() => {
    if (readOnly) return;
    api.get("/settings")
      .then(({ data }) => {
        setShippingFee(data?.shipping_fee ?? null);
        setGlobalRate(data?.quote_exchange_rate ?? null);
      })
      .catch(() => setShippingFee(null));
  }, [readOnly]);

  useEffect(() => {
    fetchDetail(selectedId);
  }, [selectedId, fetchDetail]);

  const handleCreate = async () => {
    try {
      const { data } = await api.post("/quotes", {});
      toast.success(`${t("quote.created")}: ${data.quote_number}`);
      await fetchQuotes();
      setSelectedId(data.id);
      selectQuote(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // When the edit dialog opens, prime the "共享给" section: pull the shareable-user
  // roster and seed the checkboxes from the quote's own ``shared_with`` list.
  // Sharing is per-QUOTE (2026-02 v2), not per-project — see backend Quote.shared_with.
  useEffect(() => {
    if (!editing) {
      setEditingShareWith(new Set());
      setEditingShareEditors(new Set());
      return;
    }
    setEditingShareWith(new Set((editing.shared_with || []).map((e) => String(e).toLowerCase())));
    setEditingShareEditors(new Set((editing.shared_editors || []).map((e) => String(e).toLowerCase())));
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/quotes/meta/shareable-users");
        if (cancelled) return;
        setShareUsers(data || []);
      } catch (e) {
        console.warn("Failed to load shareable users (non-blocking):", e);
      }
    })();
    return () => { cancelled = true; };
  }, [editing?.id]);

  const toggleShareUser = (email) => {
    const key = email.toLowerCase();
    setEditingShareWith((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    // Un-sharing also strips edit rights so an off-list editor doesn't leak.
    setEditingShareEditors((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // 2026-02-27 · Toggle per-user permission between 只读 and 只读和编辑.
  // ``permission`` is one of ``"read"`` / ``"edit"``.
  const setShareUserPermission = (email, permission) => {
    const key = email.toLowerCase();
    setEditingShareEditors((prev) => {
      const next = new Set(prev);
      if (permission === "edit") next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const handleSaveEdit = async () => {
    try {
      const project = (editing.project || "").trim();
      const { data } = await api.put(`/quotes/${editing.id}`, {
        quote_number: editing.quote_number || "",
        customer_name: editing.customer_name || "",
        project,
        exchange_rate: editing.exchange_rate === "" || editing.exchange_rate == null ? 0 : Number(editing.exchange_rate),
        shared_with: Array.from(editingShareWith),
        shared_editors: Array.from(editingShareEditors),
      });
      setEditing(null);
      fetchQuotes();
      fetchCustomers();
      fetchProjects();
      // 汇率变化会改变全部计算价 → 重新拉详情
      if (detail?.id === data.id) fetchDetail(data.id);
      if (activeQuote?.id === data.id) selectQuote(data);
      toast.success(t("common.save"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updateQuote = async (patch) => {
    try {
      const { data } = await api.put(`/quotes/${detail.id}`, patch);
      setDetail((d) => ({ ...d, currency: data.currency, buyer: data.buyer, customer_name: data.customer_name }));
      fetchQuotes();
      if (activeQuote?.id === data.id) selectQuote(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ----- snapshot editing (per-quote, never touches the original product) -----
  const setItemField = (itemNumber, field, value) => {
    setDetail((d) => ({
      ...d,
      items: d.items.map((it) => (it.item_number === itemNumber ? { ...it, [field]: value } : it)),
    }));
  };

  const setRowField = (itemNumber, rowId, field, value) => {
    setDetail((d) => ({
      ...d,
      items: d.items.map((it) =>
        it.item_number === itemNumber
          ? { ...it, price_rows: it.price_rows.map((r) => (r.id === rowId ? { ...r, [field]: value } : r)) }
          : it
      ),
    }));
  };

  const saveItemField = async (itemNumber, field, value) => {
    try {
      const { data } = await api.put(`/quotes/${detail.id}/items/${itemNumber}`, { [field]: value });
      setDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // ---- Per-quote product detail editor (opens on row click) ----
  const [editorState, setEditorState] = useState(null); // { startIndex } | null

  const saveRowField = async (itemNumber, rowId, field, value) => {
    try {
      const { data } = await api.put(`/quotes/${detail.id}/items/${itemNumber}/rows/${rowId}`, { [field]: value });
      setDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // 2026-06 · 按当前参数重新计算价格 — refreshes every row's frozen Final Price
  // using the CURRENT exchange rate / profit / freight / customer multiplier.
  const [recomputing, setRecomputing] = useState(false);
  const handleRecomputePrices = async () => {
    if (!detail?.id) return;
    setRecomputing(true);
    try {
      const { data } = await api.post(`/quotes/${detail.id}/recompute-prices`);
      setDetail(data);
      toast.success(t("quote.recomputePricesDone"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRecomputing(false);
    }
  };

  // 2026-06 · 采购链接管理 — list the generated links for this quote + revoke.
  const [linksOpen, setLinksOpen] = useState(false);
  const [links, setLinks] = useState([]);
  const openLinks = async () => {
    if (!detail?.id) return;
    setLinksOpen(true);
    try {
      const { data } = await api.get(`/quotes/${detail.id}/procurement-links`);
      setLinks(data.links || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const revokeLink = async (token) => {
    try {
      await api.post(`/procurement-links/${token}/revoke`);
      setLinks((ls) => ls.map((l) => (l.token === token ? { ...l, revoked: true } : l)));
      toast.success(t("quote.links.revoked"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // 2026-06 · Drag & drop reordering of the PRODUCTS inside a quote.
  const [dragItem, setDragItem] = useState(null);
  const [overItem, setOverItem] = useState(null);
  const overItemRef = useRef(null);

  const startItemDrag = (itemNumber) => (e) => {
    if (detailReadOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setDragItem(itemNumber);
    overItemRef.current = null;
    const move = (ev) => {
      // Works for BOTH views: table rows hit-test on Y only, grid cards need
      // X + Y since several cards share a row.
      const els = Array.from(document.querySelectorAll("[data-item-number]"));
      const hit = els.find((el) => {
        const b = el.getBoundingClientRect();
        const inY = ev.clientY >= b.top && ev.clientY <= b.bottom;
        if (el.tagName === "TR") return inY;
        return inY && ev.clientX >= b.left && ev.clientX <= b.right;
      });
      const num = hit?.dataset.itemNumber;
      if (num && num !== itemNumber) {
        overItemRef.current = num;
        setOverItem(num);
      }
    };
    const up = async () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      const target = overItemRef.current;
      setDragItem(null); setOverItem(null);
      if (!target || !detail?.id) return;
      const nums = (detail.items || []).map((it) => it.item_number);
      const from = nums.indexOf(itemNumber);
      const to = nums.indexOf(target);
      if (from < 0 || to < 0 || from === to) return;
      nums.splice(to, 0, nums.splice(from, 1)[0]);
      setDetail((d) => ({ ...d, items: nums.map((n) => d.items.find((it) => it.item_number === n)) }));
      try {
        const { data } = await api.post(`/quotes/${detail.id}/items/reorder`, { order: nums });
        setDetail(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  };

  // ---- Drag & drop reordering of the size rows inside one product ----  // Pointer-event based (works with mouse AND touch, unlike HTML5 DnD).
  const [dragRow, setDragRow] = useState(null);   // { item, rowId }
  const [overRow, setOverRow] = useState(null);   // { item, rowId }
  const overRowRef = useRef(null);

  const startRowDrag = (itemNumber, rowId) => (e) => {
    if (detailReadOnly) return;
    e.preventDefault();
    setDragRow({ item: itemNumber, rowId });
    overRowRef.current = null;
    const move = (ev) => {
      const trs = Array.from(document.querySelectorAll(`tr[data-item-number="${itemNumber}"][data-row-id]`));
      const hit = trs.find((tr) => {
        const b = tr.getBoundingClientRect();
        return ev.clientY >= b.top && ev.clientY <= b.bottom;
      });
      if (hit?.dataset.rowId) {
        overRowRef.current = hit.dataset.rowId;
        setOverRow({ item: itemNumber, rowId: hit.dataset.rowId });
      }
    };
    const up = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      const target = overRowRef.current;
      setDragRow(null); setOverRow(null);
      if (target) reorderRows(itemNumber, rowId, target);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  };

  const reorderRows = async (itemNumber, fromId, toId) => {
    if (!detail?.id || !fromId || !toId || fromId === toId) return;
    const item = (detail.items || []).find((it) => it.item_number === itemNumber);
    if (!item) return;
    const rows = [...(item.price_rows || [])];
    const from = rows.findIndex((r) => r.id === fromId);
    const to = rows.findIndex((r) => r.id === toId);
    if (from < 0 || to < 0) return;
    const [moved] = rows.splice(from, 1);
    rows.splice(to, 0, moved);
    setDetail((d) => ({
      ...d,
      items: d.items.map((it) => (it.item_number === itemNumber ? { ...it, price_rows: rows } : it)),
    }));
    try {
      const { data } = await api.post(`/quotes/${detail.id}/items/${itemNumber}/rows/reorder`, {
        order: rows.map((r) => r.id),
      });
      setDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Bulk select/deselect ALL price rows across ALL items — powered by the "select-all"
  // checkbox in the table header. Fires updates in parallel; the last enrich_quote
  // response wins as the source of truth.
  const toggleAllRowsSelected = async (checked) => {
    if (!detail?.id) return;
    const pairs = (detail.items || []).flatMap((it) =>
      (it.price_rows || []).map((r) => ({ item: it.item_number, rowId: r.id })),
    );
    if (!pairs.length) return;
    try {
      const results = await Promise.all(pairs.map((p) =>
        api.put(`/quotes/${detail.id}/items/${p.item}/rows/${p.rowId}`, { selected: checked }),
      ));
      setDetail(results[results.length - 1].data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Upload a photo just for this quote's line item (does NOT touch the source product).
  // Uses the shared /products/upload-image endpoint (returns {url}).
  const handleQuotePhotoReplace = async (itemNumber, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/upload-image", fd);
      await saveItemField(itemNumber, "quote_photo_url", data.url);
      toast.success(t("quote.photoReplaced"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const resetQuotePhoto = (itemNumber) => saveItemField(itemNumber, "quote_photo_url", "");

  const removeItem = async (itemNumber) => {
    try {
      await api.delete(`/quotes/${detail.id}/items/${itemNumber}`);
      fetchDetail(detail.id);
      fetchQuotes();
      if (activeQuote?.id === detail.id) refreshCount(detail.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // 2026-02-26 · Delete a single price row (size) from a quote item. Backend
  // removes the item entirely if this was its last row, so we always refetch.
  const removeRow = async (itemNumber, rowId) => {
    try {
      await api.delete(`/quotes/${detail.id}/items/${itemNumber}/rows/${rowId}`);
      fetchDetail(detail.id);
      fetchQuotes();
      if (activeQuote?.id === detail.id) refreshCount(detail.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleCopyQuote = async (q) => {
    setCopyingQuote(q.id);
    try {
      const { data } = await api.post(`/quotes/${q.id}/copy`);
      toast.success(`${t("quote.copyQuote")} → ${data.quote_number}`);
      await fetchQuotes();
      setSelectedId(data.id);
      fetchDetail(data.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCopyingQuote(null);
    }
  };


  const handleDeleteQuote = async (id) => {
    try {
      await api.delete(`/quotes/${id}`);
      if (activeQuote?.id === id) selectQuote(null);
      if (selectedId === id) {
        setSelectedId(null);
        setDetail(null);
      }
      fetchQuotes();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleSelectProducts = () => {
    selectQuote(detail);
    navigate("/");
  };

  const [exporting, setExporting] = useState(null);
  const handleExport = async (kind, ext) => {
    setExporting(kind);
    try {
      const { data } = await api.get(`/quotes/${detail.id}/export/${kind}`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(data);
      if (kind === "labels") {
        window.open(url, "_blank"); // open in a tab so the user can print directly
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${detail.quote_number}${ext}`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.exportError"));
    } finally {
      setExporting(null);
    }
  };

  const [emailing, setEmailing] = useState(false);
  const handleEmailQuote = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/quotes/${detail.id}/email`, {}, { timeout: 120000 });
      toast.success(`${t("quote.emailSent")}: ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.emailError"));
    } finally {
      setEmailing(false);
    }
  };

  // ---- Customer-template Excel export (moved here from Products page 2026-02) ----
  // Picks the customer's uploaded templates automatically from the quote's linked customer.
  const [customerTemplates, setCustomerTemplates] = useState(null); // null | {customer_name, templates[]}
  const [customerTemplatePick, setCustomerTemplatePick] = useState("");
  const [customerTemplateBusy, setCustomerTemplateBusy] = useState(false);

  const openCustomerTemplateExport = async () => {
    if (!detail?.id) return;
    try {
      const { data } = await api.get(`/quotes/${detail.id}/customer-templates`);
      if (!data.templates?.length) {
        toast.error(t("quote.customerTemplateNone").replace("{name}", data.customer_name || "-"));
        return;
      }
      if (data.templates.length === 1) {
        // Single template — no picker needed, just download
        await downloadCustomerTemplate(data.templates[0].id);
        return;
      }
      setCustomerTemplates(data);
      setCustomerTemplatePick(data.templates[0].id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.exportError"));
    }
  };

  const downloadCustomerTemplate = async (templateId) => {
    setCustomerTemplateBusy(true);
    try {
      const res = await api.post(
        `/quotes/${detail.id}/export/customer-quote`,
        { template_id: templateId },
        { responseType: "blob", timeout: 180000 },
      );
      const disp = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(disp);
      const fname = match ? match[1] : `${detail.quote_number}.xlsx`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setCustomerTemplates(null);
      toast.success(t("quote.exportSuccess"));
    } catch (e) {
      let detail = "";
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch (parseErr) {
          // Blob wasn't JSON — the outer toast falls back to the generic error message.
          console.debug("[quote export] non-JSON error blob:", parseErr);
        }
      }
      toast.error(detail || formatApiErrorDetail(e.response?.data?.detail) || t("quote.exportError"));
    } finally {
      setCustomerTemplateBusy(false);
    }
  };

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-center justify-between mb-3">
        <h1 className="font-heading text-base font-bold tracking-tight flex items-center gap-2">
          <FileText size={24} /> {t("quote.title")}
        </h1>
        <div className="flex items-center gap-2">
          {/* 2026-06 · 导出字段设置 (per user) — sits left of 新建 per user spec. */}
          {!isBuyerView && (
          <Button variant="outline" onClick={() => setExportColsOpen(true)}
            data-testid="open-export-columns" className="rounded-md">
            <Columns3 size={15} /> {t("exportCols.button")}
          </Button>
          )}
          {!readOnly && (
            <Button onClick={handleCreate} data-testid="new-quote-button" className="rounded-md bg-brand hover:bg-brand-hover transition-colors">
              <Plus size={16} /> {t("quote.new")}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:gap-6 items-stretch md:items-start">
        {/* Toggle to hide the quote list — user request Feb 2026 */}
        <button
          type="button"
          onClick={() => {
            setListCollapsed((v) => {
              const next = !v;
              localStorage.setItem("quotesys_quote_list_collapsed", next ? "1" : "0");
              return next;
            });
          }}
          data-testid="quote-list-toggle"
          title={listCollapsed ? t("quote.showList") : t("quote.hideList")}
          className="hidden md:block mt-1 shrink-0 p-2 rounded-md border border-border bg-card hover:bg-secondary transition-colors"
        >
          {listCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
        </button>
        {/* Left: Excel-style quote list — 2026-06 加宽，宽屏下所有字段完整显示
            2026-06 · 手机端：列表占满宽度；选中某单后隐藏列表、只显示详情（见下方返回栏） */}
        {!listCollapsed && (
        <aside className={`w-full md:w-[560px] xl:w-[640px] 2xl:w-[720px] md:shrink-0 bg-card border border-border rounded-md overflow-hidden ${selectedId ? "hidden md:block" : ""}`} data-testid="quotes-sidebar">
          {!readOnly && (
            <div className="border-b border-border p-2 flex flex-wrap gap-2" data-testid="quotes-filter-bar">
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger className="h-8 text-xs flex-1" data-testid="quotes-filter-customer">
                  <SelectValue placeholder={t("quote.customerFilter")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__" data-testid="quotes-filter-customer-all">{t("quote.allCustomers")}</SelectItem>
                  {customers.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`quotes-filter-customer-${c}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="h-8 text-xs flex-1" data-testid="quotes-filter-project">
                  <SelectValue placeholder={t("quote.projectFilter")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__" data-testid="quotes-filter-project-all">{t("quote.allProjects")}</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p} value={p} data-testid={`quotes-filter-project-${p}`}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* 2026-06 · 外销 filter (quote creator) — user spec. */}
              <Select value={sellerFilter} onValueChange={setSellerFilter}>
                <SelectTrigger className="h-8 text-xs flex-1 min-w-[110px]" data-testid="quotes-filter-seller">
                  <SelectValue placeholder={t("quote.sellerFilter")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__" data-testid="quotes-filter-seller-all">{t("quote.allSellers")}</SelectItem>
                  {sellers.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`quotes-filter-seller-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* 2026-07-29 · Admin scope toggle. Non-admins never see this. */}
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => setAdminScope((s) => (s === "mine" ? "all" : "mine"))}
                  className={`h-8 px-3 text-xs rounded-md border transition-colors ${
                    adminScope === "all"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card border-border hover:bg-accent"
                  }`}
                  data-testid="quotes-admin-scope-toggle"
                  title={adminScope === "all" ? t("quote.scopeAll") : t("quote.scopeMine")}
                >
                  {adminScope === "all" ? t("quote.scopeAll") : t("quote.scopeMine")}
                </button>
              )}
            </div>
          )}
          {/* 2026-06 · 采购 view gets 客户 + 外销 filters (options derived from the
              quotes already loaded — the meta endpoints are staff-only). */}
          {isBuyerView && (
            <div className="border-b border-border p-2 flex flex-wrap gap-2" data-testid="quotes-buyer-filter-bar">
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger className="h-8 text-xs flex-1 min-w-[120px]" data-testid="quotes-filter-customer">
                  <SelectValue placeholder={t("quote.customerFilter")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t("quote.allCustomers")}</SelectItem>
                  {[...new Set(quotes.map((q) => (q.customer_name || "").trim()).filter(Boolean))].sort().map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sellerFilter} onValueChange={setSellerFilter}>
                <SelectTrigger className="h-8 text-xs flex-1 min-w-[110px]" data-testid="quotes-filter-seller">
                  <SelectValue placeholder={t("quote.sellerFilter")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t("quote.allSellers")}</SelectItem>
                  {sellers.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(customerFilter !== "__all__" || sellerFilter !== "__all__") && (
                <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground"
                  onClick={() => { setCustomerFilter("__all__"); setSellerFilter("__all__"); }}
                  data-testid="quotes-buyer-clear-filters">
                  {t("pq.clearFilters")}
                </Button>
              )}
            </div>
          )}
          {/* 2026-06 · 手机端卡片列表（宽表格在手机上没法看） */}
          <ul className="md:hidden divide-y divide-border" data-testid="quotes-mobile-list">
            {filteredQuotes.length === 0 && (
              <li className="px-3 py-10 text-center text-sm text-muted-foreground" data-testid="quotes-mobile-empty">
                {t("quote.noQuotes")}
              </li>
            )}
            {filteredQuotes.map((q) => (
              <li key={q.id}>
                <button type="button" onClick={() => setSelectedId(q.id)}
                  className="w-full text-left px-3 py-3 active:bg-secondary/60 transition-colors"
                  data-testid={`quote-mobile-card-${q.quote_number}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold">{q.quote_number}</span>
                    {q.is_shared && (
                      <span className="px-1 rounded-sm bg-amber-100 text-amber-800 text-[9px] font-bold uppercase">
                        {t("quote.sharedBadge")}
                      </span>
                    )}
                    <span className="ml-auto text-[11px] text-muted-foreground font-mono">{fmtDate(q.created_at)}</span>
                  </div>
                  <div className="mt-1 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px]">
                    <span className="truncate"><span className="text-muted-foreground">{t("quote.customerName")}: </span>{q.customer_name || "-"}</span>
                    <span className="truncate"><span className="text-muted-foreground">{t("products.seller")}: </span>{q.seller_name || "-"}</span>
                    {!isBuyerView && (
                      <span className="truncate"><span className="text-muted-foreground">{t("quote.project")}: </span>{q.project || "-"}</span>
                    )}
                    <span className="font-mono"><span className="text-muted-foreground">{t("quote.itemCount")}: </span>{(q.items || []).length}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <Table data-testid="quotes-table" className="hidden md:table">
            <TableHeader>
              <TableRow>
                <TableHead className="p-0 w-8" aria-label={t("common.edit")} />
                <TableHead className="text-xs uppercase">{t("quote.quoteNumber")}</TableHead>
                <TableHead className="text-xs uppercase">{t("quote.customerName")}</TableHead>
                {!isBuyerView && <TableHead className="text-xs uppercase whitespace-nowrap">{t("products.seller")}</TableHead>}
                <TableHead className="text-xs uppercase">{isBuyerView ? t("products.seller") : t("quote.project")}</TableHead>
                <TableHead className="text-xs uppercase whitespace-nowrap">{t("quote.date")}</TableHead>
                {!isBuyerView && <TableHead className="text-xs uppercase text-right whitespace-nowrap" title={t("quote.orderProgressHint")}>{t("quote.itemCount")}</TableHead>}
                {!isBuyerView && <TableHead className="text-xs uppercase text-right w-10 whitespace-nowrap">{t("common.actions")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredQuotes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isBuyerView ? 5 : 8} className="text-center py-8 text-muted-foreground" data-testid="quotes-empty-state">
                    {t("quote.noQuotes")}
                  </TableCell>
                </TableRow>
              )}
              {filteredQuotes.map((q) => (
                <TableRow
                  key={q.id}
                  onClick={() => setSelectedId(q.id)}
                  data-testid={`quote-row-${q.quote_number}`}
                  className={`cursor-pointer ${selectedId === q.id ? "bg-brand/10 hover:bg-brand/10" : "hover:bg-secondary/40"}`}
                >
                  <TableCell className="p-0 w-8 text-center" onClick={(e) => e.stopPropagation()}>
                    {!readOnly && !q.is_shared && (
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7"
                        onClick={() => setEditing({ ...q })}
                        title={t("common.edit")}
                        data-testid={`edit-quote-${q.quote_number}`}
                      >
                        <Pencil size={13} />
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="font-mono font-semibold text-xs whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      {q.quote_number}
                      {q.is_shared && !isBuyerView && (
                        <span
                          className="inline-flex items-center px-1 py-0 rounded-sm bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider"
                          title={t("quote.sharedReadOnly")}
                          data-testid={`quote-shared-badge-${q.quote_number}`}
                        >
                          {t("quote.sharedBadge")}
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs max-w-[170px] truncate" title={q.customer_name || ""}>{q.customer_name || "-"}</TableCell>
                  {!isBuyerView && (
                    <TableCell className="text-xs max-w-[130px] truncate" title={q.seller_name || ""} data-testid={`quote-seller-${q.quote_number}`}>{q.seller_name || "-"}</TableCell>
                  )}
                  {isBuyerView ? (
                    <TableCell className="text-xs max-w-[150px] truncate" title={q.seller_name || ""} data-testid={`quote-seller-${q.quote_number}`}>{q.seller_name || "-"}</TableCell>
                  ) : (
                    <TableCell className="text-xs max-w-[150px] truncate" title={q.project || ""} data-testid={`quote-project-${q.quote_number}`}>{q.project || "-"}</TableCell>
                  )}
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(q.created_at)}</TableCell>
                  {!isBuyerView && (
                  <TableCell className="font-mono text-xs text-right" data-testid={`quote-item-count-${q.quote_number}`}>
                    {(() => {
                      const items = q.items || [];
                      const ordered = items.filter((it) => it.has_order).length;
                      if (!ordered) return items.length;
                      return (
                        <span title={items.filter((it) => it.has_order).map((it) => `${it.item_number}${it.order_number ? ` · ${it.order_number}` : ""}`).join("\n")}
                          data-testid={`quote-order-progress-${q.quote_number}`}>
                          <span className="text-emerald-600 font-bold">{ordered}</span>
                          <span className="text-muted-foreground">/{items.length}</span>
                        </span>
                      );
                    })()}
                  </TableCell>
                  )}
                  {!isBuyerView && (
                  <TableCell className="text-right p-1" onClick={(e) => e.stopPropagation()}>
                    {!readOnly && (
                    <div className="flex justify-end">
                      {/* 2026-06 · 复制报价单 (user spec) — 共享给我的报价单也能复制，
                          复制出来的是自己的新单（不继承共享设置）。 */}
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7"
                        onClick={() => handleCopyQuote(q)}
                        disabled={copyingQuote === q.id}
                        title={q.is_shared ? t("quote.copySharedQuote") : t("quote.copyQuote")}
                        data-testid={`quote-copy-${q.quote_number}`}
                      >
                        <Copy size={13} className="text-brand" />
                      </Button>
                      {!q.is_shared && (
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7"
                        onClick={() => handleDeleteQuote(q.id)}
                        title={t("common.delete")}
                        data-testid={`delete-quote-${q.quote_number}`}
                      >
                        <Trash2 size={13} className="text-destructive" />
                      </Button>
                      )}
                    </div>
                    )}
                  </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </aside>
        )}

        <QuoteEditDialog
          editing={editing}
          setEditing={setEditing}
          onSave={handleSaveEdit}
          customers={customers}
          selectedCustomer={selectedCustomerObj}
          shippingFee={shippingFee}
          globalRate={globalRate}
          projects={projects}
          onNewCustomer={() => setCustomerDialog({ customer: null })}
          onEditCustomer={() => setCustomerDialog({ customer: selectedCustomerObj })}
          shareUsers={shareUsers}
          shareWith={editingShareWith}
          shareEditors={editingShareEditors}
          onToggleShareUser={toggleShareUser}
          onSetSharePermission={setShareUserPermission}
        />

        {/* 新增 / 编辑客户 — opened from inside the quote edit dialog */}
        <CustomerQuickDialog
          open={Boolean(customerDialog)}
          customer={customerDialog?.customer || null}
          onClose={() => setCustomerDialog(null)}
          onSaved={handleCustomerSaved}
          onSavedAndCreateQuote={handleCustomerSavedAndCreateQuote}
        />

        <ProcurementLinksDialog
          open={linksOpen}
          onOpenChange={setLinksOpen}
          links={links}
          onRevoke={revokeLink}
        />

        {/* Right: products of the selected quote */}
        <section className={`flex-1 min-w-0 ${selectedId ? "" : "hidden md:block"}`} data-testid="quote-detail">
          {/* 2026-06 · 手机端返回栏 —— 回到报价单列表 */}
          {selectedId && (
            <button type="button" onClick={() => setSelectedId(null)}
              className="md:hidden mb-2 inline-flex items-center gap-1.5 text-sm text-brand font-medium"
              data-testid="quote-mobile-back">
              <PanelLeftOpen size={15} /> {t("quote.showList")}
            </button>
          )}
          {!detail ? (
            <div className="bg-card border border-border rounded-md p-12 text-center text-muted-foreground" data-testid="no-quote-selected">
              {t("quote.noQuoteSelected")}
            </div>
          ) : (
            <div className="space-y-2">
              {detail.is_shared && !isBuyerView && (
                <div
                  className={`rounded-md border px-3 py-2 text-xs flex items-center gap-2 ${detail.can_edit_shared ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-amber-300 bg-amber-50 text-amber-900"}`}
                  data-testid="quote-shared-banner"
                >
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider ${detail.can_edit_shared ? "bg-emerald-500 text-white" : "bg-amber-400 text-black"}`}>
                    {t("quote.sharedBadge")}
                  </span>
                  {detail.can_edit_shared ? t("quote.sharedEditable") : t("quote.sharedReadOnly")}
                </div>
              )}
              <div className="bg-card border border-border rounded-md px-2.5 py-1.5 flex flex-wrap items-center gap-1.5 [&_button]:h-8 [&_button]:text-xs">
                <div>
                  <p className="font-heading text-sm font-bold leading-tight">{detail.quote_number}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("quote.date")}: {fmtDate(detail.created_at)}
                    {detail.buyer ? ` · ${t("quote.buyer")}: ${detail.buyer}` : ""}
                    {detail.customer_name ? ` · ${t("quote.customer")}: ${detail.customer_name}` : ""}
                    {detail.project ? ` · ${t("quote.project")}: ${detail.project}` : ""}
                  </p>
                </div>
                {/* 2026-06 · Badge explains the rates used to compute prices:
                    USD quotes show 报价汇率 (US$→¥); CAD quotes additionally show
                    US$→C$, 海运费 and 价格系数 (user spec). */}
                <span className={`relative group inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-secondary/50 font-mono text-xs font-semibold ${isAdmin ? "cursor-help" : ""}`} data-testid="quote-currency-badge" title={isAdmin ? t("quote.currencyFromCustomer") : undefined}>
                  {(() => {
                    const isCad = (detail.currency || "USD") === "CAD";
                    const fx = Number(detail.quote_exchange_rate || 0);
                    const cadFx = Number(detail.usd_to_cad_rate || 0);
                    const ship = Number(detail.shipping_fee || 0);
                    const mult = Number(detail.price_multiplier || 1);
                    const chips = [isCad ? "CAD" : "US dollar"];
                    if (isCad && cadFx > 0) chips.push(`US$→C$ ${cadFx.toFixed(2)}`);
                    if (fx > 0) chips.push(`US$→¥ ${fx.toFixed(2)}`);
                    if (isCad && ship > 0) chips.push(`${t("quote.freightShort")} US$${ship.toFixed(0)}`);
                    if (isCad && mult !== 1) chips.push(`×${mult.toFixed(2)}`);
                    return chips.join(" · ");
                  })()}
                  {/* 2026-06 · Hover panel with the full pricing parameters —
                      admin ONLY (user spec: other roles see nothing at all). */}
                  {isAdmin && (
                  <span
                    className="pointer-events-none absolute left-0 top-full z-30 mt-1 hidden md:group-hover:block w-64 rounded-md border border-border bg-popover p-3 text-left font-sans text-xs font-normal shadow-lg"
                    data-testid="quote-pricing-params"
                  >
                    <span className="block font-semibold mb-1.5">{t("quote.pricingParams")}</span>
                    {[
                      [t("quote.paramCurrency"), detail.currency || "USD"],
                      [t("quote.paramFx"), Number(detail.quote_exchange_rate || 0).toFixed(2)],
                      ...((detail.currency === "CAD")
                        ? [[t("quote.paramCadFx"), Number(detail.usd_to_cad_rate || 0).toFixed(2)],
                           [t("quote.paramFreight"), `US$${Number(detail.shipping_fee || 0).toFixed(0)}`],
                           [t("quote.paramProfit"), `${(Number(detail.profit_rate || 0) * 100).toFixed(1)}%`],
                           [t("quote.paramMultiplier"), `×${Number(detail.price_multiplier || 1).toFixed(2)}`]]
                        : []),
                    ].map(([k, v]) => (
                      <span key={k} className="flex items-center justify-between gap-2 py-0.5">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-mono font-semibold">{v}</span>
                      </span>
                    ))}
                    {/* 2026-06 · 计算公式 admin-only (user spec). */}
                    {isAdmin && (
                    <span className="block mt-1.5 pt-1.5 border-t border-border text-[10px] text-muted-foreground" data-testid="quote-price-formula">
                      {detail.currency === "CAD" ? t("quote.paramFormulaCad") : t("quote.paramFormulaUsd")}
                    </span>
                    )}
                  </span>
                  )}
                </span>
                <div className="flex items-center rounded-md border border-border p-0.5">
                  <button onClick={() => setView("list")} data-testid="quote-list-view-button" className={`p-1.5 rounded-md transition-colors ${view === "list" ? "bg-brand text-white" : "text-muted-foreground"}`}>
                    <List size={14} />
                  </button>
                  <button onClick={() => setView("grid")} data-testid="quote-grid-view-button" className={`p-1.5 rounded-md transition-colors ${view === "grid" ? "bg-brand text-white" : "text-muted-foreground"}`}>
                    <LayoutGrid size={14} />
                  </button>
                </div>
                <Button
                  variant="outline" size="sm" onClick={() => setItemsFullscreen((v) => !v)}
                  title={t("quote.fullscreen")} data-testid="quote-items-fullscreen-button" className="rounded-md hidden md:inline-flex"
                >
                  {itemsFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                </Button>

                {/* 2026-10-07 · 回填价格到产品库（admin / export 可见；只对非只读报价） */}
                {!detailReadOnly && ["admin", "export"].includes(user?.role) && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setBackfillOpen(true)}
                    disabled={!detail?.items?.length}
                    data-testid="quote-backfill-prices-button"
                    className="rounded-md hidden md:inline-flex border-brand/40 text-brand hover:bg-brand/5"
                    title="把选中产品的新规格价格追加到产品库"
                  >
                    <ArrowUpToLine size={14} /> 回填价格
                  </Button>
                )}

                {/* 2026-06 · 手机端把整排导出/操作按钮收进底部抽屉 */}
                <QuoteMobileActions actions={[
                  // 2026-10-25 · 手机端"编辑"入口 —— 打开 QuoteEditDialog（选择客户、新增客户、
                  // 名片 OCR 自动填客户信息都在那里）。
                  !detailReadOnly && !detail?.is_shared && { key: "edit", label: t("common.edit"), icon: Pencil, onClick: () => setEditing({ ...detail }) },
                  !isBuyerView && { key: "email", label: t("quote.emailQuote"), icon: Mail, onClick: handleEmailQuote, disabled: !detail.items?.length || emailing },
                  !isBuyerView && { key: "customer-template", label: t("quote.exportCustomerTemplate"), icon: UserCheck, onClick: openCustomerTemplateExport, disabled: !detail.items?.length || customerTemplateBusy },
                  !isBuyerView && { key: "excel", label: t("quote.exportExcel"), icon: FileSpreadsheet, onClick: () => handleExport("excel", ".xlsx"), disabled: !detail.items?.length || Boolean(exporting) },
                  !isBuyerView && { key: "pdf", label: t("quote.exportPdf"), icon: FileDown, onClick: () => handleExport("pdf", ".pdf"), disabled: !detail.items?.length || Boolean(exporting) },
                  !isBuyerView && { key: "labels", label: t("quote.printLabels"), icon: Printer, onClick: () => handleExport("labels", ".pdf"), disabled: !detail.items?.length || Boolean(exporting) },
                  !detailReadOnly && { key: "links", label: t("quote.links.button"), icon: LinkIcon, onClick: openLinks },
                  !detailReadOnly && { key: "recompute", label: t("quote.recomputePrices"), icon: RefreshCw, onClick: handleRecomputePrices, disabled: !detail.items?.length || recomputing },
                  !detailReadOnly && { key: "select-products", label: t("quote.selectProducts"), icon: PackagePlus, onClick: handleSelectProducts },
                ]} />

                <div className="ml-auto hidden md:flex flex-wrap items-center gap-1.5">
                  {!isBuyerView && (
                  <Button
                    variant="outline" size="sm" disabled={!detail.items?.length || emailing}
                    onClick={handleEmailQuote}
                    data-testid="email-quote-button" className="rounded-md"
                  >
                    <Mail size={14} className="text-brand" /> {emailing ? t("quote.emailSending") : t("quote.emailQuote")}
                  </Button>
                  )}
                  {!isBuyerView && (
                  <Button
                    variant="outline" size="sm" disabled={!detail.items?.length || customerTemplateBusy}
                    onClick={openCustomerTemplateExport}
                    data-testid="export-customer-template-button" className="rounded-md"
                  >
                    <UserCheck size={14} className="text-brand" /> {customerTemplateBusy ? "..." : t("quote.exportCustomerTemplate")}
                  </Button>
                  )}
                  {!isBuyerView && (
                  <Button
                    variant="outline" size="sm" disabled={!detail.items?.length || Boolean(exporting)}
                    onClick={() => handleExport("excel", ".xlsx")}
                    data-testid="export-excel-button" className="rounded-md"
                  >
                    <FileSpreadsheet size={14} className="text-green-700" /> {t("quote.exportExcel")}
                  </Button>
                  )}
                  {!isBuyerView && (
                  <Button
                    variant="outline" size="sm" disabled={!detail.items?.length || Boolean(exporting)}
                    onClick={() => handleExport("pdf", ".pdf")}
                    data-testid="export-pdf-button" className="rounded-md"
                  >
                    <FileDown size={14} className="text-red-700" /> {t("quote.exportPdf")}
                  </Button>
                  )}
                  {!isBuyerView && (
                  <Button
                    variant="outline" size="sm" disabled={!detail.items?.length || Boolean(exporting)}
                    onClick={() => handleExport("labels", ".pdf")}
                    data-testid="print-labels-button" className="rounded-md"
                  >
                    <Printer size={14} /> {t("quote.printLabels")}
                  </Button>
                  )}
                  {!detailReadOnly && (
                  <Button
                    variant="outline" size="sm"
                    onClick={openLinks}
                    data-testid="procurement-links-button" className="rounded-md"
                  >
                    <LinkIcon size={14} className="text-brand" /> {t("quote.links.button")}
                  </Button>
                  )}
                  {!detailReadOnly && (
                  <Button
                    variant="outline" size="sm" disabled={!detail.items?.length || recomputing}
                    onClick={handleRecomputePrices}
                    data-testid="recompute-prices-button" className="rounded-md"
                    title={t("quote.recomputePricesHint")}
                  >
                    <RefreshCw size={14} className={recomputing ? "animate-spin" : "text-brand"} />
                    {recomputing ? "…" : t("quote.recomputePrices")}
                  </Button>
                  )}
                  {!detailReadOnly && (
                  <Button variant="outline" onClick={handleSelectProducts} data-testid="select-products-button" className="rounded-md">
                    <PackagePlus size={15} /> {t("quote.selectProducts")}
                  </Button>
                  )}
                </div>
              </div>

              {!detail.items?.length && (
                <div className="bg-card border border-border rounded-md p-10 text-center text-muted-foreground" data-testid="quote-empty-state">
                  {t("quote.empty")}
                </div>
              )}

              {detail.items?.length > 0 && view === "grid" && (
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="quote-products-grid">
                  {detail.items.map((p) => (
                    <div key={p.item_number}
                      data-item-number={p.item_number}
                      className={`relative bg-card border border-border rounded-md overflow-hidden transition-colors ${dragItem === p.item_number ? "opacity-40" : ""} ${overItem === p.item_number ? "ring-2 ring-brand" : ""}`}
                      data-testid={`quote-grid-card-${p.item_number}`}>
                      {!detailReadOnly && (
                      <button
                        onClick={() => removeItem(p.item_number)}
                        data-testid={`quote-grid-remove-${p.item_number}`}
                        className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-white/90 border border-border hover:bg-destructive hover:text-white transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                      )}
                      <div className="aspect-square bg-white border-b border-border flex items-center justify-center overflow-hidden p-2">
                        {(p.quote_photo_url || p.composite_photo_url || p.photo_url) ? (
                          (() => {
                            const src = p.quote_photo_url || p.composite_photo_url || p.photo_url;
                            return (
                              <img
                                src={photoThumb(src, "md")}
                                alt={p.item_number}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  const i = e.currentTarget;
                                  // Fall back once to the backend which lazily
                                  // generates the missing thumb and pushes it to R2.
                                  if (i.dataset.f !== "1") { i.dataset.f = "1"; i.src = photoThumbBackend(src, "md"); }
                                }}
                              />
                            );
                          })()
                        ) : (
                          <Package size={30} className="text-muted-foreground" />
                        )}
                      </div>
                      <div className="p-3">
                        {!detailReadOnly && (
                          <span
                            onPointerDown={startItemDrag(p.item_number)}
                            title={t("quote.itemDragToSort")}
                            data-testid={`grid-item-drag-${p.item_number}`}
                            className="absolute top-1.5 left-1.5 z-10 p-1 rounded bg-white/85 backdrop-blur cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-brand transition-colors"
                          >
                            <GripVertical size={13} />
                          </span>
                        )}
                        {/* 2026-06 · click the item number to open the product
                            editor — same behaviour as the list view. */}
                        <button
                          type="button"
                          onClick={() => setEditorState({ startIndex: (detail.items || []).findIndex((it) => it.item_number === p.item_number) })}
                          data-testid={`grid-open-editor-${p.item_number}`}
                          className="font-semibold text-sm truncate max-w-full text-left text-brand hover:underline inline-flex items-center gap-1"
                        >
                          {p.item_number}
                          <Pencil size={10} className="opacity-40" />
                        </button>
                        <p className="text-xs text-muted-foreground truncate">{p.description || "-"}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {detail.items?.length > 0 && view === "list" && isBuyerView && (
                <BuyerQuoteItems quoteId={detail.id} onItemsChange={fetchQuotes} />
              )}

              {/* 2026-06 · 外销手机端：宽表改成只读卡片流 */}
              {detail.items?.length > 0 && view === "list" && !isBuyerView && (
                <QuoteItemsMobile detail={detail} showCost={!readOnly} />
              )}

              {detail.items?.length > 0 && view === "list" && !isBuyerView && (
                <div className={itemsFullscreen ? "" : "hidden md:block"}>
                <FullscreenBox active={itemsFullscreen}>
                  {itemsFullscreen && (
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm font-bold">{detail.quote_number}</span>
                      <Button variant="outline" size="sm" className="ml-auto h-8 text-xs rounded-md"
                        onClick={() => setItemsFullscreen(false)} data-testid="quote-items-exit-fullscreen">
                        <Minimize2 size={14} /> {t("quote.exitFullscreen")}
                      </Button>
                    </div>
                  )}
                <QuoteItemsTable
                  fullscreen={itemsFullscreen}
                  detail={detail}
                  readOnly={readOnly}
                  detailReadOnly={detailReadOnly}
                  onOpenEditor={(pIdx) => setEditorState({ startIndex: pIdx })}
                  removeItem={removeItem}
                  removeRow={removeRow}
                  setItemField={setItemField}
                  saveItemField={saveItemField}
                  setRowField={setRowField}
                  saveRowField={saveRowField}
                  toggleAllRowsSelected={toggleAllRowsSelected}
                  handleQuotePhotoReplace={handleQuotePhotoReplace}
                  resetQuotePhoto={resetQuotePhoto}
                  startItemDrag={startItemDrag}
                  dragItem={dragItem}
                  overItem={overItem}
                  startRowDrag={startRowDrag}
                  dragRow={dragRow}
                  overRow={overRow}
                  fmtRowCost={fmtRowCost}
                />
                </FullscreenBox>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {/* Customer-template picker (shown only when the customer has 2+ templates) */}
      <Dialog open={!!customerTemplates} onOpenChange={(v) => { if (!v) setCustomerTemplates(null); }}>
        <DialogContent className="max-w-md" data-testid="customer-template-picker-dialog">
          <DialogHeader>
            <DialogTitle>{t("quote.exportCustomerTemplate")}</DialogTitle>
            <DialogDescription>
              {t("quote.customerTemplatePickHint").replace("{name}", customerTemplates?.customer_name || "-")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("customers.pickTemplate")}</Label>
            <select
              value={customerTemplatePick}
              onChange={(e) => setCustomerTemplatePick(e.target.value)}
              data-testid="customer-template-pick-select"
              className="w-full h-10 px-3 rounded-md border border-input bg-transparent text-sm focus:border-brand focus:outline-none"
            >
              {(customerTemplates?.templates || []).map((tk) => (
                <option key={tk.id} value={tk.id}>{tk.name}</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomerTemplates(null)} className="rounded-md">{t("common.cancel")}</Button>
            <Button
              onClick={() => downloadCustomerTemplate(customerTemplatePick)}
              disabled={!customerTemplatePick || customerTemplateBusy}
              className="rounded-md bg-brand hover:bg-brand-hover"
              data-testid="customer-template-download-button"
            >
              {customerTemplateBusy ? "..." : t("products.downloadExcel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuoteItemEditorDialog
        open={!!editorState}
        onClose={() => setEditorState(null)}
        quoteId={detail?.id}
        items={detail?.items || []}
        startIndex={editorState?.startIndex || 0}
        quoteCurrency={detail?.currency || "USD"}
        onSaved={(updated) => setDetail(updated)}
        readOnly={readOnly}
      />

      <ExportColumnsDialog open={exportColsOpen} onClose={() => setExportColsOpen(false)} />

      <BackfillPricesDialog
        open={backfillOpen}
        onOpenChange={setBackfillOpen}
        quote={detail}
      />
    </div>
  );
}
